import type { FingeringMode } from '../types/fingering';

const DB_NAME = 'fingering-db';
const DB_VERSION = 2;
const STORE_RESULTS = 'results';
const STORE_PENDING_PDFS = 'pending-pdfs';
const RESULT_KEY = 'latest';

/**
 * v1 → v2 migration 用の旧キー prefix (results store 内に置かれていた `pending-pdf:<jobId>`)。
 * onupgradeneeded 完了後は legacy migration コードだけが参照する。
 * spec: docs/superpowers/specs/2026-05-24-browser-storage-architecture-design.md §4.3
 */
const OLD_PENDING_PDF_KEY_PREFIX = 'pending-pdf:';

/** 元 PDF 先行保存の有効期限。jobLifecycleStorage の 30 分 TTL に揃える (async reload F5)。 */
export const PENDING_ORIGINAL_PDF_TTL_MS = 30 * 60 * 1000;

/**
 * 元 PDF 先行保存エントリ。
 * - 役割: ジョブ投入時に元 PDF を jobId 単位で IndexedDB に保管し、 reload 後 handleDone で originalPdfBlob に合流させる
 * - store: `pending-pdfs` (issue #29 Phase C で `results` から分離)
 * - TTL: PENDING_ORIGINAL_PDF_TTL_MS (30 分)、 超過時は purgeExpiredPendingOriginalPdfs が掃除
 */
/**
 * IndexedDB 保存形式 (issue #36 #71): Blob を直接保存する (IndexedDB は Blob をネイティブ保存可)。
 * 旧形式は number[] (Array.from(Uint8Array)) で巨大化していた → 保存窓が伸び reload 喪失の一因。
 * 読み出しは旧 number[] とも後方互換 (toBlob で吸収)。
 */
interface PendingOriginalPdf {
  data: Blob | number[];
  timestamp: number;
}

interface StoredResult {
  data: Blob | number[];
  fileName: string;
  /**
   * issue #152: アップロード時の元タイトル (拡張子なし)。 完成 PDF / MP3 / omfz 名はすべて
   * (元タイトル, part, mode) の純関数で組むため、 再生成の命名ソースとしてここに保存する。
   * fileName は「完成 PDF 名」であって元タイトルではない (混同するとサフィックスが二重化する)。
   * 旧データは欠落 (undefined) → 呼出し側が resolveOriginalTitle で fileName から derive する。
   */
  originalTitle?: string;
  audioData?: Blob | number[];
  audioFileName?: string;
  timestamp: number;
  fingeringJson?: string;
  originalPdfData?: Blob | number[];
  mxlData?: Blob | number[];
  detectedBpm?: number;
  mode?: FingeringMode;
}

/**
 * 保存値を Blob に正規化する (issue #36 #71 後方互換)。
 * 新形式は既に Blob、 旧形式は number[] なので Uint8Array 経由で Blob 化する。
 */
function toBlob(value: Blob | number[], type: string): Blob {
  if (value instanceof Blob) return value;
  return new Blob([new Uint8Array(value)], { type });
}

export interface FingeringStorageResult {
  blob: Blob;
  fileName: string;
  /** issue #152: 命名ソースとなる元タイトル (拡張子なし)。 旧データは undefined。 */
  originalTitle?: string;
  audioBlob?: Blob;
  audioFileName?: string;
  fingeringJson?: string;
  originalPdfBlob?: Blob;
  mxlBlob?: Blob;
  detectedBpm?: number;
  mode?: FingeringMode;
}

function openDB(): Promise<IDBDatabase> {
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IndexedDB is not available'));
  }
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (e) => {
      const db = request.result;
      const tx = request.transaction;
      if (!tx) {
        reject(new Error('No upgrade transaction'));
        return;
      }
      const oldVersion = (e as IDBVersionChangeEvent).oldVersion;

      if (oldVersion < 1) {
        db.createObjectStore(STORE_RESULTS);
      }
      if (oldVersion < 2) {
        const pendingStore = db.createObjectStore(STORE_PENDING_PDFS);
        // v1 旧形式 (results store 内 pending-pdf:<jobId>) を新 store へ移行。
        // migration が失敗した entry は旧 store に残し、 purgeExpiredPendingOriginalPdfs が
        // 結局 TTL 30 分で掃除する保険あり (spec §7 R-2)。
        const resultsStore = tx.objectStore(STORE_RESULTS);
        const cursorReq = resultsStore.openCursor();
        cursorReq.onsuccess = () => {
          const cursor = cursorReq.result;
          if (!cursor) return;
          const key = cursor.key;
          if (typeof key === 'string' && key.startsWith(OLD_PENDING_PDF_KEY_PREFIX)) {
            const jobId = key.slice(OLD_PENDING_PDF_KEY_PREFIX.length);
            try {
              pendingStore.put(cursor.value, jobId);
              resultsStore.delete(key);
            } catch {
              // migration 個別失敗時は旧 entry を残す
            }
          }
          cursor.continue();
        };
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export interface SaveFingeringResultOptions {
  pdfBlob: Blob;
  fileName: string;
  /** issue #152: 命名ソースとなる元タイトル (拡張子なし)。 未指定は旧データ扱い (後方互換)。 */
  originalTitle?: string;
  audioBlob?: Blob;
  audioFileName?: string;
  fingeringJson?: string;
  originalPdfBlob?: Blob;
  mxlBlob?: Blob;
  detectedBpm?: number;
  mode?: FingeringMode;
}

export async function saveFingeringResult(options: SaveFingeringResultOptions): Promise<void> {
  const {
    pdfBlob,
    fileName,
    originalTitle,
    audioBlob,
    audioFileName,
    fingeringJson,
    originalPdfBlob,
    mxlBlob,
    detectedBpm,
    mode,
  } = options;

  // issue #52: originalPdfBlob は後段の usePdfBackground で pdfjsLib.getDocument({ data })
  // 経由で arrayBuffer() が呼ばれる。 iOS Safari は IndexedDB SQLite 外領域に backing される
  // Blob の参照を喪失し NotFoundError を投げる既知問題があるため、 number[] (SQLite テキスト
  // 領域) で保存して回避する。 transaction open 前に arrayBuffer 評価 (transaction auto-commit
  // 取りこぼし防止) を必ず先行する。
  let originalPdfData: number[] | undefined;
  if (originalPdfBlob) {
    originalPdfData = Array.from(new Uint8Array(await originalPdfBlob.arrayBuffer()));
  }

  const db = await openDB();

  // issue #36 #71: Blob を直接保存する (巨大 number[] 化を撤廃)。 保存窓を最小化し
  // 「表示済み・保存未コミット」窓での reload 喪失を減らす。 IndexedDB は Blob をネイティブ保存可。
  // issue #52: originalPdfData のみ number[] 化済 (上記コメント参照、 他 Blob は arrayBuffer 経路
  // を持たないため Blob のまま維持)。
  const stored: StoredResult = { data: pdfBlob, fileName, timestamp: Date.now() };

  if (originalTitle !== undefined) {
    stored.originalTitle = originalTitle;
  }
  if (audioBlob) {
    stored.audioData = audioBlob;
    stored.audioFileName = audioFileName;
  }
  if (fingeringJson) {
    stored.fingeringJson = fingeringJson;
  }
  if (originalPdfData) {
    stored.originalPdfData = originalPdfData;
  }
  if (mxlBlob) {
    stored.mxlData = mxlBlob;
  }
  if (detectedBpm !== undefined) {
    stored.detectedBpm = detectedBpm;
  }
  if (mode !== undefined) {
    stored.mode = mode;
  }

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_RESULTS, 'readwrite');
    tx.objectStore(STORE_RESULTS).put(stored, RESULT_KEY);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

export async function loadFingeringResult(): Promise<FingeringStorageResult | null> {
  let db: IDBDatabase;
  try {
    db = await openDB();
  } catch {
    return null;
  }

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_RESULTS, 'readonly');
    const req = tx.objectStore(STORE_RESULTS).get(RESULT_KEY);
    req.onsuccess = () => {
      db.close();
      const stored = req.result as StoredResult | undefined;
      if (!stored) {
        resolve(null);
        return;
      }
      // issue #36 #71: 新形式 (Blob) はそのまま、 旧形式 (number[]) は toBlob で吸収する (後方互換)。
      const blob = toBlob(stored.data, 'application/pdf');
      const result: FingeringStorageResult = { blob, fileName: stored.fileName };

      if (stored.originalTitle !== undefined) {
        result.originalTitle = stored.originalTitle;
      }
      if (stored.audioData) {
        result.audioBlob = toBlob(stored.audioData, 'audio/mpeg');
        result.audioFileName = stored.audioFileName;
      }
      if (stored.fingeringJson) {
        result.fingeringJson = stored.fingeringJson;
      }
      if (stored.originalPdfData) {
        result.originalPdfBlob = toBlob(stored.originalPdfData, 'application/pdf');
      }
      if (stored.mxlData) {
        result.mxlBlob = toBlob(stored.mxlData, 'application/vnd.recordare.musicxml');
      }
      if (stored.detectedBpm !== undefined) {
        result.detectedBpm = stored.detectedBpm;
      }
      if (stored.mode !== undefined) {
        result.mode = stored.mode;
      }

      resolve(result);
    };
    req.onerror = () => { db.close(); reject(req.error); };
  });
}

export async function clearFingeringResult(): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_RESULTS, 'readwrite');
    tx.objectStore(STORE_RESULTS).delete(RESULT_KEY);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

/**
 * ジョブ投入時に元 PDF を jobId 単位で先行保存する (async reload F5、設計 §5.2 案A)。
 * issue #29 Phase C で pending-pdfs 専用 store に分離。
 */
export async function savePendingOriginalPdf(jobId: string, pdfBlob: Blob): Promise<void> {
  const db = await openDB();
  // issue #36 #71: Blob を直接保存 (巨大 number[] 化を撤廃)。
  const entry: PendingOriginalPdf = {
    data: pdfBlob,
    timestamp: Date.now(),
  };

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_PENDING_PDFS, 'readwrite');
    tx.objectStore(STORE_PENDING_PDFS).put(entry, jobId);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

/** 先行保存した元 PDF を jobId で読み出す。未保存のときは null。 */
export async function loadPendingOriginalPdf(jobId: string): Promise<Blob | null> {
  let db: IDBDatabase;
  try {
    db = await openDB();
  } catch {
    return null;
  }

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_PENDING_PDFS, 'readonly');
    const req = tx.objectStore(STORE_PENDING_PDFS).get(jobId);
    req.onsuccess = () => {
      db.close();
      const entry = req.result as PendingOriginalPdf | undefined;
      if (!entry) {
        resolve(null);
        return;
      }
      // issue #36 #71: 新形式 Blob はそのまま、 旧 number[] は toBlob で吸収。
      resolve(toBlob(entry.data, 'application/pdf'));
    };
    req.onerror = () => { db.close(); reject(req.error); };
  });
}

/** 先行保存した元 PDF を 1 件削除する。ジョブ失敗時 / 結果合流後の purge に使う。 */
export async function clearPendingOriginalPdf(jobId: string): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_PENDING_PDFS, 'readwrite');
    tx.objectStore(STORE_PENDING_PDFS).delete(jobId);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

/**
 * TTL (30 分) を超過した元 PDF 先行保存エントリを掃除する。
 * issue #29 Phase C で pending-pdfs 専用 store に対する cursor 走査に簡素化された
 * (旧実装の `key.startsWith('pending-pdf:')` チェックは不要)。
 */
export async function purgeExpiredPendingOriginalPdfs(): Promise<void> {
  const db = await openDB();
  const threshold = Date.now() - PENDING_ORIGINAL_PDF_TTL_MS;

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_PENDING_PDFS, 'readwrite');
    const store = tx.objectStore(STORE_PENDING_PDFS);
    const cursorReq = store.openCursor();
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (!cursor) return;
      const entry = cursor.value as PendingOriginalPdf;
      if (entry.timestamp < threshold) {
        cursor.delete();
      }
      cursor.continue();
    };
    cursorReq.onerror = () => { db.close(); reject(cursorReq.error); };
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}
